import { CalendarClock } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import type { CloseOfBooksUnavailable } from '@/types/connectors';

interface Props { cob?: CloseOfBooksUnavailable | null; }

/** #2228 — warning callout for a Yahoo DSP run whose close-of-books checks
 *  failed. The check fails open: a failed `/cob` call counts the day as closed
 *  and the run goes ahead, so the run stays green while the walkback that keeps
 *  unfinalized days out never ran. Prod returned 401 on every check for weeks
 *  with nothing on screen; this panel is that signal. */
export function CloseOfBooksUnavailablePanel({ cob }: Props) {
  if (!cob || !(cob.checks_failed > 0)) return null;
  const checks = cob.checks_failed === 1 ? '1 close-of-books check' : `${cob.checks_failed} close-of-books checks`;
  const advertisers = cob.advertiser_ids?.length ?? 0;
  return (
    <Alert variant="warning" data-testid="cob-unavailable-panel">
      <CalendarClock className="h-4 w-4" aria-hidden />
      <AlertTitle>{checks} failed — the run went ahead without them</AlertTitle>
      <AlertDescription>
        <p className="mb-1.5 text-foreground">
          Yahoo did not say whether the latest days are closed
          {advertisers > 0 && ` for ${advertisers === 1 ? '1 advertiser' : `${advertisers} advertisers`}`}, so
          the run treated them as closed. Figures for the most recent day may change when Yahoo
          finalizes it; the next run re-pulls it.
        </p>
        {cob.auth_failures > 0 && (
          <p className="mb-1.5 text-foreground">
            {cob.auth_failures === cob.checks_failed ? 'All of them were' : `${cob.auth_failures} were`}{' '}
            authentication failures (401): Yahoo rejected the credential for close-of-books.
          </p>
        )}
        {cob.first_error && (
          <span className="block break-words font-mono text-xs text-foreground">{cob.first_error}</span>
        )}
      </AlertDescription>
    </Alert>
  );
}
